// billing-copy.ts — pure copy for the plan-change confirmation dialog (dashboard-shell feedback
// cycle, Task 2). `Billing.tsx` used to fire `POST /api/billing/checkout` straight off a single
// click, upgrade OR downgrade, with no confirmation at all — unlike "Cancel subscription", which
// already sits behind one. The two outcomes that route actually has (its own doc comment,
// `apps/api/src/routes/billing.ts`) are not obvious from the button alone, so the dialog has to
// state them correctly instead of guessing:
//
//   - The workspace already has an entitled (paid, non-canceling) subscription: the switch is
//     IN PLACE via `updateSubscription` — no checkout, no new order (this is exactly what Johnny
//     saw on Polar: switching starter→growth→starter left no order behind). It takes effect
//     immediately, but Polar bills the prorated difference on the NEXT invoice, not today.
//   - The workspace has no entitled subscription (on Free, or fully canceled): there is no
//     subscription to switch, so this is a brand-new hosted Polar checkout — a real charge,
//     just made on Polar's own page rather than this one.
//
// `currentPlanKey === 'free'` is the exact same fork `PlanCard`'s `pendingLabel` already keys
// off ("Redirecting…" vs "Switching…", `Billing.tsx`) — reusing it here means the dialog copy
// and the button's own in-flight label can never disagree about which branch is about to run.
//
// `t: Catalog` (locale-dashboard plan, Task 12) is a REQUIRED field on the params object — no
// default — same policy as every other `t: Catalog` parameter in this plan: this is a pure
// function, not a component, so it cannot resolve its own copy via `useT()`. Every literal below
// moved to `en.billing.planChangeConfirm.*`; the actual English text produced is unchanged, which
// is what `billing-copy.test.ts`'s money-safety regexes still assert against.
import { PLAN_LIMITS } from '@repo/shared-types';
import type { PlanCode } from '@repo/shared-types';
import type { Catalog } from '../locales/en';

export interface PlanChangeConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
}

export function planChangeConfirmCopy(params: {
  currentPlanKey: PlanCode;
  targetPlanKey: PlanCode;
  isUpgrade: boolean;
  t: Catalog;
}): PlanChangeConfirmCopy {
  const { currentPlanKey, targetPlanKey, isUpgrade, t } = params;
  const targetName = PLAN_LIMITS[targetPlanKey].name;
  const targetPrice = PLAN_LIMITS[targetPlanKey].priceUsd;
  const verb = isUpgrade
    ? t.billing.planChangeConfirm.verbUpgrade
    : t.billing.planChangeConfirm.verbSwitch;

  if (currentPlanKey === 'free') {
    return {
      title: t.billing.planChangeConfirm.title({ verb, name: targetName }),
      description: t.billing.planChangeConfirm.checkoutDescription({
        name: targetName,
        price: targetPrice,
      }),
      confirmLabel: t.billing.planChangeConfirm.continueToPolarCta,
    };
  }

  return {
    title: t.billing.planChangeConfirm.title({ verb, name: targetName }),
    description: t.billing.planChangeConfirm.switchDescription({
      name: targetName,
      price: targetPrice,
    }),
    confirmLabel: t.billing.planChangeConfirm.confirmNow({ verb }),
  };
}
